<template>
  <bulma-table class="table is-bordered is-striped is-narrow is-fullwidth"
    :columns="columns"
    :page-index="filter.pageIndex"
    :page-size="filter.pageSize"
    :total-rows="totalRows"
    :is-loading="isTableLoading"
    :sort-column="filter.sortColumn"
    :sort-order="filter.sortOrder"
    :draggable="false"
    :show-pagination="false"
    :is-title-checkable="editable"
    title-checkbox-location="front"
    :is-title-checked="selectAllChecked"
    @title-checkbox-changed="selectAllItems"
    @pageChanged="onPageChange"
    @sort="sort">
    <tr v-for="(item, index) in activeItems"
      :class="isItemDeleted(item) ? 'strike-through-line' : ''"
      :key="item.quoteItemId">
      <td v-if="editable"
        class="has-vertical-middle is-content-width">
        <div class="pretty p-icon p-curve p-smooth m-0 p-bigger">
          <input v-model="item.isSelected"
            type="checkbox">
          <div class="state p-primary">
            <i class="icon mdi mdi-check" />
            <label />
          </div>
        </div>
      </td>
      <td v-show="$userInfo.isSupportUser"
        class="has-vertical-middle has-text-right linenumber-input">
        <vue-numeric v-if="item.isAdded"
          class="input has-text-right"
          :class="defaultRowColourClass(item)"
          v-model.number="item.lineNumber"
          :precision="0"
          separator="" />
        <span v-else>{{ item.lineNumber }}</span>
      </td>
      <td class="has-vertical-middle itemno-input">
        <input v-if="item.isAdded"
          class="input"
          :class="[defaultRowColourClass(item), $v.activeItems.$each[index].itemNo.required ? '' : 'is-danger']"
          placeholder="Item No."
          v-model="item.itemNo">
        <span v-else>{{ item.itemNo }}</span>
      </td>
      <td :class="['has-vertical-middle itemdesc-input']">
        <input v-if="item.isAdded"
          class="input"
          :class="[defaultRowColourClass(item), $v.activeItems.$each[index].itemDesc.required ? '' : 'is-danger']"
          placeholder="Item Description"
          v-model="item.itemDesc">
        <span v-else>{{ item.itemDesc }}</span>
      </td>
      <td class="has-vertical-middle has-text-centered">
        <div v-if="item.isAdded"
          class="pretty p-icon p-curve p-smooth m-0 p-bigger">
          <input v-model="item.blend"
            type="checkbox"
            @change="updateBlend(item)">
          <div class="state p-primary">
            <i class="icon mdi mdi-check" />
            <label />
          </div>
        </div>
        <span v-else
          class="icon has-text-info">
          <i class="mdi mdi-24px"
            :class="[ item.blend ? 'mdi-check' : '' ]" />
        </span>
      </td>
      <td class="has-vertical-middle has-text-centered">
        <div v-if="editable && !isItemDeleted(item)"
          class="pretty p-icon p-curve p-smooth m-0 p-bigger">
          <input v-model="item.reportOnly"
            type="checkbox">
          <div class="state p-primary">
            <i class="icon mdi mdi-check" />
            <label />
          </div>
        </div>
        <span v-else
          class="icon has-text-info">
          <i class="mdi mdi-24px"
            :class="[ item.reportOnly ? 'mdi-check' : '' ]" />
        </span>
      </td>
      <td v-if="innerValue.quotingMethod === quotingMethodTypes.Hour"
        class="has-vertical-middle has-text-right money-input">
        <vue-numeric v-if="editable && !isItemDeleted(item)"
          class="input has-text-right"
          :class="defaultRowColourClass(item)"
          v-model.number="item.rate"
          :precision="2"
          @value-changed="updateValue(index, item)" />
        <span v-else>{{ $filters.formatNumber(item.rate, $userInfo.locale) }}</span>
      </td>
      <td class="has-vertical-middle has-text-right money-input">
        <vue-numeric v-if="editable && !isItemDeleted(item)"
          class="input has-text-right"
          :class="defaultRowColourClass(item)"
          v-model.number="item.hourValue"
          :minus="true"
          :precision="2"
          @blur="updateValue(index, item)" />
        <span v-else>{{ $filters.formatNumber(item.hourValue, $userInfo.locale) }}</span>
      </td>
      <td class="has-vertical-middle has-text-right">{{ $filters.formatNumber(item.dollarValue, $userInfo.locale) }}</td>
      <td v-if="isNtar"
        class="has-vertical-middle has-text-right is-content-width">
        <span>{{ $filters.formatNumber(item.loadingPercent, $userInfo.locale, 0) }}%</span>
      </td>
      <td class="has-vertical-middle has-text-centered">
        <div v-if="item.presentAs">
          <div v-for="split in item.presentAs"
            :key="split.itemType">
            <div class="tags has-addons mb-0 present-as-tags">
              <span :class="`tag mb-1 is-${split.itemType.toLowerCase()}`">{{ split.itemType }}</span>
              <span class="tag is-dark mb-1">{{ split.percent }}%</span>
            </div>
          </div>
        </div>
      </td>
      <td v-show="$userInfo.isSupportUser"
        class="has-vertical-middle has-text-centered is-content-width">{{ item.itemStatus }}
      </td>
      <td v-if="editable"
        class="has-vertical-middle has-text-centered is-content-width">
        <div class="icon-container">
          <button v-if="!isItemDeleted(item) && item.isDeleted !== true && item.deleted !== true"
            class="button is-danger is-small is-inverted tooltip"
            @click="deleteItem(item, index)"
            :disabled="innerValue.readOnly || isItemDeleted(item)"
            data-tooltip="Delete">
            <span class="icon is-small">
              <i class="mdi mdi-delete mdi-24px" />
            </span>
          </button>
        </div>
      </td>
    </tr>
    <template v-if="!editable"
      slot="empty">
      <section class="section">
        <div class="content has-text-grey has-text-centered">
          <span icon="icon is-large">
            <i class="mdi mdi-48px mdi-emoticon-sad" />
          </span>
          <p>Nothing</p>
        </div>
      </section>
    </template>
    <template slot="footer">
      <tr>
        <th class="left-right-items"
          :colspan="totalColSpan">
          <div v-if="editable"
            class="left-item">
            <button class="button is-info is-rounded is-outlined"
              data-tooltip="add item"
              @click="addBlankItem()">
              <span class="icon">
                <i class="mdi mdi-plus" />
              </span>
              <span>Add Item</span>
            </button>
          </div>
          <div v-if="activeItems.length > 0"
            class="has-text-right has-text-success right-item">
            Totals
          </div>
        </th>
        <th v-if="activeItems.length > 0"
          class="has-text-right has-text-success"
          :class="[ categoryTotal >= 0 ? 'has-text-success' : 'has-text-danger' ]">{{ $filters.formatCurrency(categoryTotal, $userInfo.locale) }}</th>
        <th colspan="100%" />
      </tr>
      <tr v-if="editable">
        <td class="has-text-right revalue"
          :colspan="totalColSpan">
          <div>
            <button class="button is-small revalue-button"
              data-tooltip="add item"
              @click="revalueCategory()">
              <span>Revalue</span>
            </button>
            <button class="button  is-small is-info sign-button"
              @click="revalueCategory()">
              $
            </button>
          </div>
        </td>
        <td>
          <div class="field has-addons">
            <vue-numeric class="input has-text-right"
              :precision="2"
              v-model="revalueValue" />
          </div>
        </td>
        <td colspan="100%" />
      </tr>
    </template>
  </bulma-table>
</template>

<script>
import { QuoteItemModel } from '@/classes/viewmodels'
import { required } from 'vuelidate/lib/validators'
import { QuotingMethodTypes, LabourTimeTypes, ItemCategoryTypes } from '@/enums'
import { LabourColumns } from '../columns'
import BulmaTable from '@/components/BulmaTable'
import _cloneDeep from 'lodash/cloneDeep'
import { QuoteLabourMixin, QuoteAssessmentMixin } from '../mixins'
import VueNumeric from '@/components/VueNumeric'

export default {
  name: 'AuthoriseLabourOld',
  components: {
    BulmaTable,
    VueNumeric
  },
  mixins: [QuoteLabourMixin, QuoteAssessmentMixin],
  props: {
    value: null,
    vehicle: null,
    originalValue: null,
    itemType: {
      type: String,
      default: ''
    },
    editable: {
      type: Boolean,
      default: false
    },
    itemsList: {
      type: Array,
      default: () => []
    },
    nextLineNumber: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      innerValue: this.value,
      isTableLoading: false,
      filter: {
        sortColumn: LabourColumns[1].name,
        sortOrder: LabourColumns[1].defaultOrder,
        pageIndex: 1,
        pageSize: 100
      },
      revalueValue: 0
    }
  },
  validations: {
    activeItems: {
      $each: {
        itemNo: {
          required
        },
        itemDesc: {
          required
        }
      }
    }
  },
  computed: {
    columns() {
      const c = _cloneDeep(LabourColumns)
      c.find((c) => c.name === 'itemType').isVisible = false
      if (this.innerValue.quotingMethod === QuotingMethodTypes.Dollar) {
        c[6].title = 'Amount'
        c.splice(5, 1)
      } else if (this.innerValue.quotingMethod === QuotingMethodTypes.Hour) {
        c[6].title = LabourColumns[6].title
      }
      if (!this.isNtar) {
        c.splice(c.length - 4, 1)
      }
      c[0].isVisible = this.$userInfo.isSupportUser
      c[c.length - 2].isVisible = this.$userInfo.isSupportUser
      if (!this.editable) {
        c.splice(c.length - 1, 1)
      }
      return c
    },
    quotingMethodTypes() {
      return QuotingMethodTypes
    },
    isNtar() {
      return (
        this.innerValue.labourType === LabourTimeTypes.NTAR ||
        this.innerValue.labourType === LabourTimeTypes.LTAR ||
        this.innerValue.labourType === LabourTimeTypes.eMTA
      )
    },
    selectAllChecked() {
      if (this.activeItems.every((item) => item.isSelected)) {
        return true
      } else if (this.activeItems.some((item) => item.isSelected)) {
        return null
      } else {
        return false
      }
    },
    totalRows() {
      if (this.editable) {
        return this.activeItems.length + 1
      }
      return this.activeItems.length
    },
    categoryTotal() {
      switch (this.itemType) {
        case ItemCategoryTypes.RR:
          return this.calculateItemCategoryTotal(this.innerValue.quotingMethod, this.activeItems, ItemCategoryTypes.RR)
        case ItemCategoryTypes.RWA:
          return this.calculateItemCategoryTotal(this.innerValue.quotingMethod, this.activeItems, ItemCategoryTypes.RWA)
        case ItemCategoryTypes.REP:
          return this.calculateItemCategoryTotal(this.innerValue.quotingMethod, this.activeItems, ItemCategoryTypes.REP)
        case ItemCategoryTypes.PAINT:
          return this.calculateItemCategoryTotal(this.innerValue.quotingMethod, this.activeItems, ItemCategoryTypes.PAINT)
      }
    },
    totalColSpan() {
      let length = this.isNtar ? this.columns.length - 6 : this.columns.length - 5
      if (this.$userInfo.isSupportUser) {
        length++
      }
      return length
    },
    activeItems() {
      return this.itemsList.filter((i) => i.itemType === this.itemType)
    }
  },
  watch: {
    value(newVal) {
      this.innerValue = newVal
      this.defaultSelectItems()
    },
    categoryTotal(newValue) {
      if (newValue) {
        this.revalueValue = newValue
      }
    }
  },
  created() {
    this.revalueValue = this.categoryTotal
  },
  mounted() {},
  methods: {
    onPageChange() {},
    sort() {},
    updateTotal(index, item) {},
    deleteItem(item, index) {
      item.deleted = true
    },
    isItemDeleted(item) {
      return item.deleted || item.isDeleted
    },
    selectAllItems(value) {
      if (value !== null) {
        this.activeItems.forEach((item) => {
          item.isSelected = value
        })
      }
    },
    defaultSelectItems() {
      this.activeItems.forEach((item) => {
        item.isSelected = false
      })
    },
    addBlankItem() {
      let rate = 0
      // const type = ItemCategoryTypes.RR
      switch (this.itemType) {
        case ItemCategoryTypes.RR:
          rate = this.isNtar
            ? this.innerValue.shopRate
            : this.getLabourRate(ItemCategoryTypes.RR, this.innerValue.rates, this.innerValue.shopRate, this.innerValue.quoteId).rate
          break
        case ItemCategoryTypes.RWA:
          rate = this.isNtar
            ? this.innerValue.shopRate
            : this.getLabourRate(ItemCategoryTypes.RWA, this.innerValue.rates, this.innerValue.shopRate, this.innerValue.quoteId).rate
          break
        case ItemCategoryTypes.REP:
          rate = this.isNtar
            ? this.innerValue.shopRate
            : this.getLabourRate(ItemCategoryTypes.REP, this.innerValue.rates, this.innerValue.shopRate, this.innerValue.quoteId).rate
          break
        case ItemCategoryTypes.PAINT:
          rate = this.isNtar
            ? this.innerValue.shopRate
            : this.getLabourRate(this.vehicle.paintGroup, this.innerValue.rates, this.innerValue.shopRate, this.innerValue.quoteId).rate
          break
      }
      const sortNo = this.innerValue.labours.length ? Math.max(...this.innerValue.labours.map((i) => i.sortNo)) + 1 : 1
      let newItem = new QuoteItemModel(this.innerValue.quoteId, '', '', this.itemType)
      newItem.lineNumber = this.nextLineNumber
      newItem.itemNo = this.$filters.pad(newItem.lineNumber, 4)
      newItem.hourValue = 0
      newItem.rate = rate
      newItem.dollarValue = 0
      newItem.opgCode = this.vehicle.paintGroup
      newItem.sortNo = sortNo
      newItem.isNew = true
      newItem.isAdded = true
      newItem.isSelected = true
      this.innerValue.labours.splice(this.innerValue.labours.length, 1, newItem)
      this.innerValue.lines = this.nextLineNumber + 1
      // this.$toast.open({
      //   message: 'Item added',
      //   type: 'is-success',
      //   position: 'is-top',
      //   queue: false
      // })
      this.$notification.success('','Item added')

    },
    updateBlend(item) {
      const isBlend = item.blend
      if (item.itemType === ItemCategoryTypes.PAINT && this.isNtar) {
        const value = isBlend ? roundAwayFromZero(item.hourValue * 0.7) : roundAwayFromZero(item.hourValue / 0.7)
        if (this.value.quotingMethod === QuotingMethodTypes.Dollar) {
          item.hourValue = value
          item.dollarValue = item.hourValue
        } else {
          item.hourValue = value
          item.dollarValue = roundAwayFromZero(item.hourValue * item.rate)
        }
        this.updateTotal()
      }
    },
    revalueCategory() {
      this.revalue(this.revalueValue, this.categoryTotal, this.activeItems)
    }
  }
}
</script>

<style lang="scss" scoped>
.item-type-tag {
  width: 52px;
}
.present-as-tags {
  display: inline-flex;
  .tag {
    font-size: 0.6rem !important;
  }
}
</style>